import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const SPECIALIZATION_MAP = {
  Doctor: [
    "General Medicine", "Cardiology", "Neurology", "Orthopedics", "Dermatology",
    "Gynecology", "Pediatrics", "Ophthalmology", "ENT", "Gastroenterology",
    "Urology", "Nephrology", "Oncology", "Endocrinology", "Pulmonology",
    "Rheumatology", "Hematology", "Psychiatry", "Radiology", "Anesthesiology",
    "Emergency Medicine", "Geriatrics", "Sports Medicine", "Plastic Surgery", "Other"
  ],
  Therapist: [
    "Physical Therapy", "Occupational Therapy", "Speech Therapy", "Cognitive Behavioral Therapy",
    "Psychotherapy", "Marriage & Family Therapy", "Art Therapy", "Music Therapy",
    "Respiratory Therapy", "Radiation Therapy", "Nutritional Therapy", "Behavioral Therapy",
    "Play Therapy", "Trauma Therapy", "Group Therapy", "Other"
  ],
};

const DOCTOR_MGMT_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

  .dm-root {
    padding: 24px;
    font-family: 'Outfit', sans-serif;
    color: #1e293b;
  }

  .dm-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(20px);
    padding: 32px 40px;
    border-radius: 32px;
    border: 1px solid #fff;
    box-shadow: 0 20px 50px rgba(186, 230, 253, 0.3);
    margin-bottom: 40px;
  }

  .dm-title { font-size: 32px; font-weight: 800; color: #0c4a6e; margin: 0; }
  .dm-subtitle { font-size: 14px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 1.5px; }

  .dm-btn {
    padding: 14px 28px;
    border-radius: 14px;
    border: none;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .dm-btn-primary {
    background: linear-gradient(135deg, #0284c7, #0ea5e9);
    color: #fff;
    box-shadow: 0 10px 25px rgba(2, 132, 199, 0.3);
  }
  .dm-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 15px 35px rgba(2, 132, 199, 0.4); }

  /* ── Search ── */
  .dl-search-wrap {
    display: flex;
    justify-content: center;
    position: relative;
    z-index: 2;
    margin-bottom: 30px;
  }
  .dl-search-inner {
    position: relative;
    width: 100%;
    max-width: 580px;
  }
  .dl-search-input {
    width: 100%;
    padding: 20px 24px 20px 24px;
    border-radius: 60px;
    border: 2px solid #bae6fd;
    font-size: 16px;
    font-family: 'Outfit', sans-serif;
    background: rgba(255, 255, 255, 0.9);
    color: #0c4a6e;
    backdrop-filter: blur(20px);
    outline: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 12px 40px rgba(186, 230, 253, 0.4);
    text-align: center;
  }
  .dl-search-input::placeholder { color: #94a3b8; }
  .dl-search-input:focus {
    border-color: #0284c7;
    box-shadow: 0 16px 50px rgba(186, 230, 253, 0.6);
    transform: scale(1.02);
  }

  .dl-stats {
    text-align: center;
    font-size: 14px;
    color: #64748b;
    margin-bottom: 40px;
  }
  .dl-stats strong { color: #0284c7; font-weight: 700; }

  /* ── Grid ── */
  .dl-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 24px;
    position: relative;
    z-index: 1;
  }

  /* ── Card ── */
  .dl-card-outer {
    position: relative;
    border-radius: 24px;
    padding: 2px;
    background: white;
    transition: all 0.4s cubic-bezier(.22,.68,0,1.2);
    box-shadow: 0 10px 30px rgba(186, 230, 253, 0.2);
  }
  .dl-card-outer:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(186, 230, 253, 0.4);
  }

  .dl-card-ring {
    position: absolute;
    inset: 0;
    border-radius: 24px;
    padding: 2px;
    background: conic-gradient(
      from var(--angle, 0deg),
      transparent 0deg,
      #0284c7 60deg,
      #7c3aed 120deg,
      #0ea5e9 180deg,
      transparent 240deg
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }
  .dl-card-outer:hover .dl-card-ring {
    opacity: 1;
    animation: spin-light 2s linear infinite;
  }
  @property --angle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
  @keyframes spin-light { to { --angle: 360deg; } }

  .dl-card-inner {
    background: #ffffff;
    border-radius: 22px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
    border: 1px solid #f0f9ff;
  }

  .dl-card-img-wrap {
    position: relative;
    overflow: hidden;
    height: 160px;
    flex-shrink: 0;
  }
  .dl-card-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
  }
  .dl-card-outer:hover .dl-card-img { transform: scale(1.05); }

  .dl-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 11px;
    font-weight: 800;
    padding: 6px 14px;
    border-radius: 50px;
    backdrop-filter: blur(12px);
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    transition: transform 0.2s;
    border: none;
  }
  .dl-badge:hover { transform: scale(1.05); }
  .dl-badge-active { background: rgba(220, 252, 231, 0.9); color: #16a34a; border: 1px solid #bbf7d0; }
  .dl-badge-inactive { background: rgba(254, 226, 226, 0.9); color: #dc2626; border: 1px solid #fecaca; }

  .dl-card-body {
    padding: 20px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .dl-card-name { font-size: 18px; font-weight: 800; color: #0c4a6e; margin-bottom: 2px; }
  .dl-card-spec { font-size: 13px; font-weight: 700; color: #0284c7; text-transform: uppercase; margin-bottom: 6px; }
  .dl-card-exp { font-size: 12px; color: #64748b; margin-bottom: 12px; }

  .dl-divider { height: 1px; background: #f1f5f9; margin-bottom: 12px; }

  .dl-card-meta { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
  .dl-card-meta-row { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #475569; }
  .dl-card-meta-icon { width: 28px; height: 28px; border-radius: 8px; background: #f0f9ff; display: flex; align-items: center; justify-content: center; font-size: 12px; border: 1px solid #e0f2fe; }

  .dl-card-fee { display: flex; align-items: center; justify-content: space-between; background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 12px; padding: 8px 14px; margin-bottom: 16px; }
  .dl-card-fee-label { font-size: 11px; color: #64748b; font-weight: 500; }
  .dl-card-fee-value { font-size: 18px; font-weight: 800; color: #0369a1; }

  .dl-actions { display: flex; gap: 8px; margin-top: auto; }
  .dl-action-btn { flex: 1; padding: 10px; border-radius: 12px; border: none; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.3s; box-shadow: 0 4px 15px rgba(0,0,0,0.05); }
  .dl-action-edit { background: linear-gradient(135deg, #0284c7, #0ea5e9); color: white; }
  .dl-action-edit:hover { background: linear-gradient(135deg, #0ea5e9, #0284c7);; transform: translateY(-2px); }
  .dl-action-delete { background: white; color: #dc2626; border: 1px solid #fecaca; }
  .dl-action-delete:hover { background: #fee2e2; transform: translateY(-2px); }

  .dm-input {
    width: 100%; padding: 14px 18px; border-radius: 14px; border: 2px solid #f1f5f9; background: #f8fafc; font-size: 14px; font-weight: 600; color: #1e293b; outline: none; transition: all 0.3s ease;
  }
  .dm-input:focus { border-color: #0284c7; background: #fff; box-shadow: 0 0 0 4px rgba(2, 132, 199, 0.1); }
  /* ── Toggle Switch ── */
.dl-toggle {
  position: relative;
  width: 70px;
  height: 34px;
  border-radius: 50px;
  background: #e2e8f0;
  cursor: pointer;
  transition: background 0.3s ease;
  display: flex;
  align-items: center;
  padding: 4px;
}

.dl-toggle.active {
  background: linear-gradient(135deg, #22c55e, #16a34a);
}

.dl-toggle.inactive {
  background: linear-gradient(135deg, #f87171, #dc2626);
}

.dl-toggle-circle {
  width: 26px;
  height: 26px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 4px 10px rgba(0,0,0,0.2);
  transition: transform 0.3s cubic-bezier(.22,.68,0,1.2);
}

.dl-toggle.active .dl-toggle-circle {
  transform: translateX(36px);
}

.dl-toggle.inactive .dl-toggle-circle {
  transform: translateX(0);
}

/* Label below toggle */
.dl-toggle-label {
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  margin-top: 6px;
  color: #475569;
}

  /* Modal */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 500; padding: 24px;
  }
  .modal-content {
    background: #fff; width: 100%; max-width: 800px; border-radius: 32px; padding: 40px; max-height: 90vh; overflow-y: auto; box-shadow: 0 40px 100px rgba(15, 23, 42, 0.3); position: relative;
  }
  .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
  .form-group { display: flex; flex-direction: column; gap: 8px; }
  .form-label { font-size: 13px; font-weight: 700; color: #475569; }

  @media (max-width: 768px) {
    .dm-header { flex-direction: column; align-items: stretch; padding: 24px 20px; text-align: center; gap: 16px; }
    .dl-search-input { padding: 14px 20px; font-size: 14px; }
    .dl-grid { grid-template-columns: 1fr; }
    .modal-content { padding: 24px; border-radius: 20px; }
    .form-grid { grid-template-columns: 1fr; }
  }
`;


const EMPTY_FORM = {
  name: "", email: "", password: "", category: "Doctor", specialization: "",
  experience: "", consultation_fee: "",
  availability_start: "", availability_end: "", slot_duration: "",
  address: "", city: "", is_active: true,
};

const EMPTY_CLINIC = { name: "", location: "" };
const EMPTY_ACHIEVEMENT = { title: "", file: null, preview: "" };

export default function Doctors() {
  const [doctors, setDoctors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [search, setSearch] = useState("");
  const [togglingId, setTogglingId] = useState(null);
  const [clinics, setClinics] = useState([{ ...EMPTY_CLINIC }]);
  const [achievements, setAchievements] = useState([]);

  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const fetchDoctors = async () => {
    try {
      const res = await fetch(`${API}/api/doctors/all`);
      const data = await res.json();
      setDoctors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch doctors", err);
      setDoctors([]);
    }
  };

  const styleInjected = useRef(false);
  useEffect(() => {
    if (!styleInjected.current) {
      const tag = document.createElement("style");
      tag.textContent = DOCTOR_MGMT_STYLES;
      document.head.appendChild(tag);
      styleInjected.current = true;
    }
    fetchDoctors();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();
    if (!formData.id && !formData.password) {
      toast.error("Password is required for new doctor!");
      return;
    }
    const payload = { ...formData };
    if (formData.id && !formData.password) delete payload.password;

    Object.keys(payload).forEach((key) => {
      if (["clinics", "achievements_data", "profile_image"].includes(key)) return;
      if (key === "is_active") form.append(key, payload[key] ? "true" : "false");
      else form.append(key, payload[key]);
    });
    if (imageFile) form.append("profile_image", imageFile);

    // Append multiple clinics
    form.append("clinics", JSON.stringify(clinics));

    // Append achievements (title + optional image)
    const achData = achievements.map((a, i) => ({ title: a.title, index: i }));
    form.append("achievements_data", JSON.stringify(achData));
    achievements.forEach((a) => {
      if (a.file) form.append("achievements", a.file);
    });


    const url = payload.id ? `${API}/api/doctors/${payload.id}` : `${API}/api/doctors`;
    const method = payload.id ? "PUT" : "POST";

    try {
      const res = await fetch(url, { method, body: form });
      if (!res.ok) {
        const data = await res.json();
        if (data.message?.includes("duplicate") || data.code === "ER_DUP_ENTRY") {
          toast.error("A doctor with this email already exists!");
          return;
        }
        throw new Error(data.message || "Failed to save doctor");
      }
      await res.json();
      fetchDoctors();
      setShowForm(false);
      toast.success(`Doctor ${formData.id ? "updated" : "added"} successfully!`);
      setFormData({ ...EMPTY_FORM });
      setClinics([{ ...EMPTY_CLINIC }]);
      setAchievements([]);
      setImageFile(null);
    } catch (err) {
      toast.error(err.message || "Failed to save doctor");
    }
  };
  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${API}/api/doctors/bulk-upload`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.message);
      if (data.inserted === 0) {
        toast.warning("⚠️ No new doctors added (duplicates found)");
      } else {
        toast.success(`✅ ${data.inserted} added | ❌ ${data.skipped} skipped`);
      }

      fetchDoctors(); // refresh list
    } catch (err) {
      toast.error(err.message || "Upload failed");
    }
  };

  const toggleStatus = async (id) => {
    if (togglingId === id) return; // prevent double click
    setTogglingId(id);
    try {
      const res = await fetch(`${API}/api/doctors/${id}/toggle`, { method: "PUT" });
      const data = await res.json();

      if (res.status === 400 && data.ongoing) {
        toast.error(`⚠️ Can't deactivate — ${data.meeting.patient_name}'s appointment is in progress!`, { autoClose: 6000 });
        return;
      }
      if (!res.ok) {
        toast.error(data.message || "Failed to toggle status");
        return;
      }

      setDoctors((prev) => prev.map((doc) => doc.id === id ? { ...doc, is_active: !!data.is_active } : doc));

      if (!data.is_active) {
        if (data.notified > 0) {
          toast.success(`Doctor deactivated — ${data.notified} patient(s) emailed to choose reassignment or rebook ✅`, { autoClose: 6000 });
        } else {
          toast.info("Doctor deactivated — no upcoming appointments affected.", { autoClose: 4000 });
        }
      } else {
        toast.success("Doctor is active again ✅", { autoClose: 3000 });
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setTogglingId(null); // re-enable after done
    }
  };

  const filteredDoctors = doctors.filter((doc) => {
    const searchText = search.toLowerCase();
    return (
      doc.name?.toLowerCase().includes(searchText) ||
      doc.specialization?.toLowerCase().includes(searchText) ||
      doc.clinic_name?.toLowerCase().includes(searchText) ||
      doc.email?.toLowerCase().includes(searchText) ||
      doc.city?.toLowerCase().includes(searchText)
    );
  });

  return (
    <div className="dm-root">
      {/* Refined Header */}
      <div className="dm-header">
        <div className="flex flex-col gap-2">
          <p className="dm-subtitle">Network Administration</p>
          <h1 className="dm-title">Doctor Management</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setFormData({ ...EMPTY_FORM });
              setClinics([{ ...EMPTY_CLINIC }]);
              setAchievements([]);
              setShowForm(true);
            }}
            className="dm-btn dm-btn-primary"
          >
            <span style={{ fontSize: '20px' }}>+</span> Add New Specialist
          </button>
          {/* BULK IMPORT */}

          <label
            className="dm-btn dm-btn-primary"
            style={{ background: "#10b981", color: "#fff", cursor: "pointer" }}
          >
            📂Bulk Upload Doctors
            <input
              type="file"
              accept=".xlsx, .csv"
              title="Upload Excel file to add multiple doctors"
              hidden
              onChange={handleBulkUpload}
            />
          </label>
        </div>
      </div>

      {/* Modern Search */}
      <div className="dl-search-wrap">
        <div className="dl-search-inner">
          <input
            type="text"
            placeholder="Search by name, clinic, or city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="dl-search-input"
          />
        </div>
      </div>

      <div className="dl-stats">
        <strong>{filteredDoctors.length}</strong> specialists found in network
      </div>

      {/* Grid Layout replacing Table */}
      {filteredDoctors.length === 0 ? (
        <div style={{ padding: '64px', textAlign: 'center', opacity: 0.6 }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧭</div>
          <p style={{ fontWeight: 800, fontSize: '18px' }}>No Specialists Found</p>
        </div>
      ) : (
        <div className="dl-grid">
          {filteredDoctors.map((doc) => {
            let firstClinic = null;
            try {
              const parsedClinics = typeof doc.clinics === 'string' ? JSON.parse(doc.clinics) : (doc.clinics || []);
              if (parsedClinics.length > 0) firstClinic = parsedClinics[0];
            } catch (e) {}

            return (
            <div key={doc.id} className="dl-card-outer">
              <div className="dl-card-ring" />
              <div className="dl-card-inner">
                <div className="dl-card-img-wrap">
                  <img
                    className="dl-card-img"
                    src={
                      doc.profile_image
                        ? `${API}/uploads/doctors/${doc.profile_image}`
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(doc.name || "Doctor")}&background=bae6fd&color=0369a1&size=400`
                    }
                    alt={doc.name}
                  />
                  <div className={`dl-badge ${doc.is_active ? "dl-badge-active" : "dl-badge-inactive"}`}>
                    {doc.is_active ? "Available" : "Unavailable (Emergency Work)"}
                  </div>
                </div>

                <div className="dl-card-body">
                  <h2 className="dl-card-name">{doc.name || "Dr. Anonymous"}</h2>
                  <p className="dl-card-spec">{doc.category || "Doctor"} • {doc.specialization}</p>
                  <p className="dl-card-exp">{doc.experience ? `${doc.experience} yrs exp.` : "Experienced"}</p>

                  <div className="dl-divider" />

                  <div className="dl-card-meta">
                    <div className="dl-card-meta-row">
                      <span className="dl-card-meta-icon">🏢</span>
                      <span>{firstClinic?.name || doc.clinic_name || "Private Practice"}</span>
                    </div>
                    <div className="dl-card-meta-row">
                      <span className="dl-card-meta-icon">📍</span>
                      <span>{firstClinic?.location || doc.city || "Not set"}</span>
                    </div>
                    <div className="dl-card-meta-row">
                      <span className="dl-card-meta-icon">🕒</span>
                      <span>{doc.availability_start} - {doc.availability_end}</span>
                    </div>
                  </div>

                  <div className="dl-card-fee">
                    <span className="dl-card-fee-label">Consultation</span>
                    <span className="dl-card-fee-value">₹{doc.consultation_fee ?? "—"}</span>
                  </div>


                  <div className="dl-actions" style={{ flexDirection: "column" }}>

                    {/* 🔄 STATUS TOGGLE BUTTON */}
                    <button
                      className="dl-action-btn"
                      style={{
                        background: doc.is_active ? "#fef3c7" : "#dcfce7",
                        color: doc.is_active ? "#92400e" : "#166534",
                        border: doc.is_active ? "1px solid #fde68a" : "1px solid #bbf7d0",
                        opacity: togglingId === doc.id ? 0.6 : 1,
                        cursor: togglingId === doc.id ? "not-allowed" : "pointer",
                      }}
                      onClick={() => toggleStatus(doc.id)}
                      disabled={togglingId === doc.id}
                    >
                      {togglingId === doc.id
                        ? "Please wait..."
                        : doc.is_active
                          ? "Set Unavailable (Emergency Work)"
                          : "Set Available Again"}
                    </button>

                    {/* EXISTING ACTION BUTTONS */}
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        className="dl-action-btn dl-action-edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData({ ...EMPTY_FORM, ...doc, is_active: doc.is_active });
                          // parse existing clinics for edit
                          try {
                            const parsed = doc.clinics ? JSON.parse(doc.clinics) : [];
                            setClinics(parsed.length ? parsed : [{ ...EMPTY_CLINIC }]);
                          } catch { setClinics([{ ...EMPTY_CLINIC }]); }
                          // parse existing achievements for edit
                          try {
                            const parsedAch = doc.achievements_data ? JSON.parse(doc.achievements_data) : [];
                            setAchievements(parsedAch.map(a => ({ title: a.title, file: null, preview: a.image || "" })));
                          } catch { setAchievements([]); }
                          setShowForm(true);
                          setImageFile(null);
                        }}
                      >
                        ✏️ Edit
                      </button>

                      <button
                        className="dl-action-btn dl-action-delete"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (window.confirm(`Are you sure you want to permanently delete ${doc.name}?`)) {
                            const res = await fetch(`${API}/api/doctors/${doc.id}`, { method: "DELETE" });
                            if (res.ok) {
                              fetchDoctors();
                              toast.success("Doctor deleted");
                            } else toast.error("Failed to delete");
                          }
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
          })}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', borderBottom: '1.5px solid #f1f5f9', paddingBottom: '24px' }}>
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#0c4a6e', margin: 0 }}>
                  {formData.id ? "Update Profile" : "Register Specialist"}
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: '11px', fontWeight: 700, color: '#0284c7', textTransform: 'uppercase', letterSpacing: '1px' }}>Network Portal Access</p>
              </div>
              <button onClick={() => setShowForm(false)} style={{ background: '#f1f5f9', border: 'none', width: '36px', height: '36px', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: 800, color: '#64748b' }}>✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input name="name" value={formData.name} placeholder="Dr. Smith" onChange={handleChange} required className="dm-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input name="email" value={formData.email} placeholder="doctor@example.com" onChange={handleChange} required className="dm-input" />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Password {formData.id && "(Leave blank to keep current)"}</label>
                  <input type="password" name="password" value={formData.password || ""} placeholder="Security key" onChange={handleChange} className="dm-input" />
                </div>

                {/* ── Specialist Type ── */}
                <div className="form-group">
                  <label className="form-label">Specialist Type</label>
                  <select
                    name="category"
                    value={formData.category || "Doctor"}
                    onChange={(e) => {
                      setFormData({ ...formData, category: e.target.value, specialization: "" });
                    }}
                    className="dm-input"
                  >
                    <option value="Doctor">Doctor</option>
                    <option value="Therapist">Therapist</option>
                  </select>
                </div>

                {/* ── Department Specialization ── */}
                <div className="form-group">
                  <label className="form-label">Department / Specialization</label>
                  <select
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleChange}
                    className="dm-input"
                    required
                  >
                    <option value="">— Select specialization —</option>
                    {(SPECIALIZATION_MAP[formData.category || "Doctor"] || []).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Experience (Yrs)</label>
                  <input name="experience" type="number" value={formData.experience || ""} placeholder="7" onChange={handleChange} className="dm-input" />
                </div>

                <div className="form-group">
                  <label className="form-label">Consultation Fee (₹)</label>
                  <input name="consultation_fee" type="number" value={formData.consultation_fee || ""} placeholder="800" onChange={handleChange} className="dm-input" />
                </div>

                {/* ── Clinics (multi) ── */}
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label className="form-label" style={{ margin: 0 }}>Clinic(s)</label>
                    <button
                      type="button"
                      onClick={() => setClinics([...clinics, { ...EMPTY_CLINIC }])}
                      style={{ background: 'linear-gradient(135deg,#0284c7,#0ea5e9)', color: '#fff', border: 'none', borderRadius: '10px', padding: '6px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                    >
                      + Add Clinic
                    </button>
                  </div>
                  {clinics.map((clinic, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px', marginBottom: '10px', alignItems: 'center', background: '#f8fafc', padding: '12px', borderRadius: '14px', border: '1.5px solid #e0f2fe' }}>
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '4px' }}>Clinic Name</label>
                        <input
                          value={clinic.name}
                          placeholder="e.g. Center Health"
                          onChange={(e) => {
                            const updated = [...clinics];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setClinics(updated);
                          }}
                          className="dm-input"
                          style={{ marginBottom: 0 }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '4px' }}>Location / Address</label>
                        <input
                          value={clinic.location}
                          placeholder="e.g. Anna Nagar, Chennai"
                          onChange={(e) => {
                            const updated = [...clinics];
                            updated[idx] = { ...updated[idx], location: e.target.value };
                            setClinics(updated);
                          }}
                          className="dm-input"
                          style={{ marginBottom: 0 }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setClinics(clinics.filter((_, i) => i !== idx))}
                        disabled={clinics.length === 1}
                        style={{ background: clinics.length === 1 ? '#f1f5f9' : '#fee2e2', color: clinics.length === 1 ? '#cbd5e1' : '#dc2626', border: 'none', borderRadius: '10px', width: '36px', height: '36px', fontSize: '16px', cursor: clinics.length === 1 ? 'not-allowed' : 'pointer', marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Remove clinic"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                <div className="form-group">
                  <label className="form-label">Schedule Start</label>
                  <input name="availability_start" type="time" value={formData.availability_start || ""} onChange={handleChange} className="dm-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Schedule End</label>
                  <input name="availability_end" type="time" value={formData.availability_end || ""} onChange={handleChange} className="dm-input" />
                </div>

                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Profile Image</label>
                  <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} style={{ padding: '12px', background: '#f8fafc', borderRadius: '12px', border: '2px dashed #e2e8f0', cursor: 'pointer' }} />
                </div>

                {/* ── Achievements (optional, multi) ── */}
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div>
                      <label className="form-label" style={{ margin: 0 }}>Achievements</label>
                      <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '8px' }}>Optional</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAchievements([...achievements, { ...EMPTY_ACHIEVEMENT }])}
                      style={{ background: 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff', border: 'none', borderRadius: '10px', padding: '6px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                    >
                      + Add Achievement
                    </button>
                  </div>

                  {achievements.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '20px', background: '#faf5ff', borderRadius: '14px', border: '1.5px dashed #d8b4fe', color: '#a855f7', fontSize: '13px', fontWeight: 600 }}>
                      🏆 No achievements added yet — click &ldquo;+ Add Achievement&rdquo; to begin
                    </div>
                  )}

                  {achievements.map((ach, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '10px', marginBottom: '10px', alignItems: 'flex-end', background: '#faf5ff', padding: '14px', borderRadius: '14px', border: '1.5px solid #e9d5ff' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                          <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '4px' }}>Achievement Title</label>
                          <input
                            value={ach.title}
                            placeholder="e.g. Best Cardiologist Award 2023"
                            onChange={(e) => {
                              const updated = [...achievements];
                              updated[idx] = { ...updated[idx], title: e.target.value };
                              setAchievements(updated);
                            }}
                            className="dm-input"
                            style={{ marginBottom: 0 }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '4px' }}>Certificate / Image <span style={{ color: '#a855f7' }}>(optional)</span></label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const f = e.target.files[0];
                              if (!f) return;
                              const updated = [...achievements];
                              updated[idx] = { ...updated[idx], file: f, preview: URL.createObjectURL(f) };
                              setAchievements(updated);
                            }}
                            style={{ padding: '8px', background: '#fff', borderRadius: '10px', border: '1.5px dashed #c4b5fd', cursor: 'pointer', width: '100%', fontSize: '12px' }}
                          />
                          {ach.preview && (
                            <img src={ach.preview} alt="preview" style={{ marginTop: '8px', height: '60px', borderRadius: '8px', objectFit: 'cover', border: '1.5px solid #d8b4fe' }} />
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAchievements(achievements.filter((_, i) => i !== idx))}
                        style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '10px', width: '36px', height: '36px', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        title="Remove achievement"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: '40px', display: 'flex', gap: '16px', justifyContent: 'flex-end', borderTop: '1.5px solid #f1f5f9', paddingTop: '32px' }}>
                <button type="button" onClick={() => setShowForm(false)} className="dm-btn" style={{ background: '#f1f5f9', color: '#64748b' }}>Discard</button>
                <button type="submit" className="dm-btn dm-btn-primary">Confirm Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}